/**
 * useDocumentDownload — hook to fetch an original document from GridFS
 * via the JWT-authenticated /api/documents/download/{id} endpoint and
 * trigger a browser download.
 *
 * Q5 · Public-App Un-hide (2026-02-09): operator directive to enable
 * "Download original document" affordance on the KA so investors can
 * pull the raw PDFs for concepts / sub-topics / documents. The backend
 * endpoint (routes/documents.py L266) already exists and streams from
 * GridFS — this hook layers on the JWT the browser wouldn't send with
 * a raw <a href> click.
 *
 * Usage:
 *   const { downloadDocument, downloading, error } = useDocumentDownload();
 *   <button onClick={() => downloadDocument(docId, filename)}>Download</button>
 *
 * The hook manages a per-download loading state so multiple simultaneous
 * downloads render the correct pending indicator. `error` is null when
 * OK and a string when the last download failed (401/404/500/network).
 */
import { useCallback, useState } from 'react';
import { API, getToken } from '../utils/authClient';

export function useDocumentDownload() {
  const [downloading, setDownloading] = useState({});  // { [docId]: true }
  const [error, setError] = useState(null);

  const downloadDocument = useCallback(async (docId, suggestedFilename = null) => {
    if (!docId) return;
    setDownloading((prev) => ({ ...prev, [docId]: true }));
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(`${API}/documents/download/${encodeURIComponent(docId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        // Read the JSON error detail if present, otherwise a generic message.
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) detail = j.detail;
        } catch { /* not JSON */ }
        throw new Error(detail);
      }
      const blob = await res.blob();

      // Prefer the filename the server put on Content-Disposition; fall back
      // to the caller-supplied one; fall back to the doc id.
      let filename = suggestedFilename || docId;
      const cd = res.headers.get('content-disposition') || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      if (match?.[1]) filename = match[1];

      // Trigger browser download.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Download failed');
    } finally {
      setDownloading((prev) => {
        const next = { ...prev };
        delete next[docId];
        return next;
      });
    }
  }, []);

  return { downloadDocument, downloading, error };
}

export default useDocumentDownload;
